/* ================================================================
 * Copyright © 2025 Tobeunicorn Co., Ltd. All rights reserved.
 *
 * 프로젝트명 : ArmyLink V1.0
 * 설명 : 관심표명자 전역저장소
 * 버전 : 1.0
 * 파일명 : InterestedStore.tsx
 * 작성자 : 강동형
 * 작성일 : 2025-03-01
 * ================================================================ 
 * 수정 : 2025-07-18, 강동형 : 저작권 추가, 기본값 변경
 * 수정 : 2025-12-18, 강동형 : 파일 업로드 관련 스토어 값 추가
 * 수정 : 2026-02-02, 강동형 : 학과 추가
 * 수정 : 2026-02-03, 강동형 : useInterestedStore, Reset함수 구조 개선
 *================================================================= */

import { interested } from 'src/types/InterestedType'
import {create} from 'zustand'

const today = new Date()
const oneMonthAgo = new Date();    
oneMonthAgo.setMonth(today.getMonth() - 1);
const nineteenYearsAgo = new Date();
nineteenYearsAgo.setFullYear(today.getFullYear() - 19);

export interface InterstedFilterState {
    isOpen : boolean,
    start_date:string | Date | null;
    end_date:string | Date | null;
    start_birth:string | Date | null;
    end_birth:string | Date | null;
    temp_start_date:string | Date | null;
    temp_end_date:string | Date | null;
    temp_start_birth:string | Date | null;
    temp_end_birth:string | Date | null;
    edu_level: number[];
    highschool_type: number[];
    gender : number[];
    status : number[];
    temp_edu_level: number[];
    temp_highschool_type: number[];
    temp_gender : number[];
    temp_status : number[];
    page_Num:number;
    sort_by : string;
    sort_order : string
    student_name: string;
    element:any;
    elementValue:string;
    setOpen: (value:boolean) =>void;
    setField: (field: keyof InterstedFilterState, value: any) => void
    toggleFilter: (category: "temp_edu_level" | "temp_highschool_type" | "temp_status" |"temp_gender", value: number, defaultValue: number) => void;
    syncField: (field:keyof InterstedFilterState,to: "temp" | "base", from: "temp" | "base") => void;
    reset: (open?: boolean) => void;
    resetField: (field: keyof InterstedFilterState) => void;
  }

const InterestedFilterDefault = {
  isOpen : false,
  start_date: '',
  end_date: '',
  start_birth: '',
  end_birth: '',
  temp_start_date: '',
  temp_end_date: '',
  temp_start_birth: '',
  temp_end_birth: '',
  edu_level: [0],
  highschool_type: [0],
  gender : [0],
  status: [0],
  temp_edu_level: [0],
  temp_highschool_type: [0],
  temp_gender : [0],
  temp_status: [0],
  page_Num:1,
  sort_by:'counsel_date',
  sort_order:'desc',
  student_name:'',
  element:'',
  elementValue:''
}

export const useInterestedFilterStore = create<InterstedFilterState>((set)=>{
    return {
      ...InterestedFilterDefault,
    setOpen : (value : boolean) => set({isOpen:value}),
    setField: (field,value)=>set((state)=>({...state,[field]:value})),
    toggleFilter: (category, value, defaultValue) =>
      set((state) => {
        const currentValues = state[category];
        let updatedValues;
  
        if (value === defaultValue) {
          // ✅ "전체"를 누르면 기존 값 제거하고 "전체"만 남김
          updatedValues = [defaultValue];
        } else {
          // 우선 "전체"(기본값)를 제거한 후
        const filteredValues = currentValues.filter((v) => v !== defaultValue);
  
        // 이미 선택된 값이면 제거, 없으면 추가
        if (filteredValues.includes(value)) {
          updatedValues = filteredValues.filter((v) => v !== value);
          // 모든 값이 제거되면 기본값으로 복원
          if (updatedValues.length === 0) updatedValues = [defaultValue];
        } else {
          updatedValues = [...filteredValues, value];
        }
        }
  
        let newState = { ...state, [category]: updatedValues };
  
        // edu_level이 변경되면 고등학교 관련 필드 업데이트
      if (category === "temp_edu_level") {
        // 고등학교(1)가 선택되지 않은 경우 highschool_type을 기본값(0)으로 초기화
        if (!updatedValues.includes(1)) {
          newState.highschool_type = [defaultValue];
        }

      }
  
        return newState;
      }),
  syncField: (field: string, to: "temp" | "base", from: "temp" | "base") =>
    set((state) => {
        const fieldMap: Record<string, [keyof InterstedFilterState, keyof InterstedFilterState]> = {
          gender: ["temp_gender", "gender"],
          status: ["temp_status", "status"],
          edu_level: ["temp_edu_level", "edu_level"],
          highschool_type: ["temp_highschool_type", "highschool_type"],
        };

        const pair = fieldMap[field];
        if (!pair) {
        console.warn(`"${field}" is not a valid syncable field`);
        return {};
        }

        const [tempKey, baseKey] = pair;
        const toKey = to === "temp" ? tempKey : baseKey;
        const fromKey = from === "temp" ? tempKey : baseKey;

        return {
        [toKey]: state[fromKey],
        };
    }),
  reset: (open = false) =>
      set(() => ({
        ...InterestedFilterDefault,
        isOpen: open
      })),
  resetField: (field) => set((state) => ({...state,[field]: InterestedFilterDefault[field as keyof typeof InterestedFilterDefault]})),
  }})

interface intersetedState extends interested{
    setField: (field: keyof intersetedState, value: any) => void,
    response: any
    total_rows:number
    isCoverdBy:'UploadFile' | null
    tempFile : File | null
    setResponse: (data: any, page?:number) => void;
    reset: (field?: keyof intersetedState) => void
}

// ✅ 초기값을 “단일 소스”로 관리
const useInterestedStoreDefault = {
  counsel_date: today,
  id: 0,
  birthday: today,
  phone: "",
  status: "",
  school_id: "",
  education_id: "",
  name: "",
  gender: 0,
  memo: "",
  major: "",
  total_rows: 0,
  response: [],
  isCoverdBy: null,
  tempFile: null
};


export const useInterestedStore = create<intersetedState>((set) => ({
  ...useInterestedStoreDefault,
  setField: (field,value)=>set((state)=>({...state,[field]:value})),
  setResponse: (data: any[], page = 1) =>
    set((state) => {
      // 배열 내 객체들을 각각 추출
      if (page === 1) {
        return {
          response: data,
        };
      }
  
      return {
        response: [
          ...(state.response ?? []),
          ...data,
        ],
      };
    }),
  // ✅ reset 제대로: 전체 or 특정 필드
  reset: (field) =>
    set((state) => {
      if (!field) {
        return { ...useInterestedStoreDefault };
      }
      return { ...state, [field]: (useInterestedStoreDefault as any)[field] };
    }),
}));